import { useState } from "react";
import {
  BriefcaseIcon,
  UserCheckIcon,
  UserIcon,
  UserPlusIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  ExternalGrantStatusEnum,
  type EntityFragment,
} from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Select } from "@/components/molecules/Select";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import {
  useChangeExternalGrantRole,
  useExternalGrants,
  useInviteExternalGrantee,
  useRevokeExternalGrant,
  useVerifiedFirms,
} from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { useEntityRoles } from "@/components/organisms/Roles/Roles.hooks";
import { describePermissions } from "@/components/organisms/Roles/Roles.tools";
import { RolesSelect } from "@/components/organisms/Roles/RolesSelect";
import { EnumColors } from "@/constants/values";
import { useScope } from "@/hooks/useScope";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { SettingsViewCard, SettingsViewLayout } from "./SettingsViewLayout";

import { Input } from "@/components/ui/input";

type VerifiedFirm = ReturnType<typeof useVerifiedFirms>["firms"][number];
type Grant = ReturnType<typeof useExternalGrants>["given"][number];

/**
 * The books of each business the person owns - their own books among them -
 * shared with the firms it works with: a bookkeeper through the year, an
 * accountant at its end, each asked by the address of whoever there takes the
 * books up, or a firm Neoledger verified by its name, whose owners do - with
 * one of the business's roles, the most the firm may do there. Once shared,
 * who at the firm works in them and as what, the role changed and the books
 * taken back. Below it, the books others share with the person, opened from
 * their clients.
 */
export function SettingsAccountantView() {
  const navigate = useNavigate();
  const { accounts } = useScope();
  const { given, received, isLoading } = useExternalGrants();
  // Shared by its owners alone: the businesses the person owns, their own
  // books first.
  const owned = (accounts ?? [])
    .filter((account) => account?.isOwner && account.entity)
    .map((account) => account!.entity!)
    .sort(
      (one, other) => Number(other.isUserEntity) - Number(one.isUserEntity),
    );
  const standing = received.filter(
    (grant) => grant.grantStatus === ExternalGrantStatusEnum.Active,
  );

  return (
    <SettingsViewLayout title="Your accountant">
      <div className="flex flex-col gap-3 p-4">
        {owned.map((entity) => (
          <SettingsAccountantBusiness
            key={entity.identifier}
            entity={entity}
            grants={given.filter(
              (grant) => grant.entityIdentifier === entity.identifier,
            )}
            isLoading={isLoading}
          />
        ))}

        {standing.length > 0 && (
          <SettingsViewCard
            icon={UserCheckIcon}
            title="Books shared with you"
            description={`${standing.length} ${standing.length === 1 ? "business shares its" : "businesses share their"} books with a firm you're in.`}
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app/clients")}
              >
                Your clients
              </Button>
            }
          />
        )}
      </div>
    </SettingsViewLayout>
  );
}

/**
 * One business's books and the firms they are shared with: a row for each -
 * the firm, or the address asked; asked or shared; the business's role it is
 * held to, changed here; and who at the firm works in them - then a row to
 * share them with another.
 */
function SettingsAccountantBusiness({
  entity,
  grants,
  isLoading,
}: {
  entity: EntityFragment;
  grants: Grant[];
  isLoading: boolean;
}) {
  const { roles, isLoading: rolesLoading } = useEntityRoles(entity.id);
  const { invite, loading: isInviting } = useInviteExternalGrantee();
  const { changeRole } = useChangeExternalGrantRole();
  const { revoke, loading: isRevoking } = useRevokeExternalGrant();
  const { firms, isLoading: isLoadingFirms } = useVerifiedFirms();
  const [emailAddress, setEmailAddress] = useState("");
  // Asked of someone by their address, or of a verified firm: one of the two.
  const [firm, setFirm] = useState<VerifiedFirm | null>(null);
  // The business's role the firm is held to: picked, never guessed for her.
  const [pickedRole, setPickedRole] = useState<string | null>(null);
  const books = entity.isUserEntity ? "Your own books" : entity.name;

  return (
    <SettingsViewCard
      icon={BriefcaseIcon}
      title={books}
      description={
        grants.length === 0
          ? `Share ${entity.isUserEntity ? "your own books" : `${entity.name}'s books`} with your accountant's or bookkeeper's firm: its people work in them as members - never changing the plan or members, or closing the business - and see only the chats you share with them. It's free.`
          : `Shared with ${grants.length} firm${grants.length === 1 ? "" : "s"}. Each may do no more than its role here allows.`
      }
      isLoading={isLoading}
      data-testid={`settings-accountant-${entity.identifier}`}
    >
      {grants.map((grant) => {
        const isShared = grant.grantStatus === ExternalGrantStatusEnum.Active;
        return (
          <div
            key={grant.identifier}
            data-testid={`settings-accountant-grant-${grant.identifier}`}
            className="flex flex-col [border-top-width:1px] [border-top-style:solid] border-border"
          >
            <div className="flex items-center gap-3 pl-3 pr-3 min-h-11 pt-1.5 pb-1.5 flex-wrap">
              <div className="flex text-muted-foreground/80">
                <Icon ReactIcon={BriefcaseIcon} size="md" />
              </div>
              <div className="flex-1 min-w-0">
                <Typography level="body-md" className="truncate">
                  {grant.granteeName ?? grant.invitedEmailAddress}
                </Typography>
                <Typography
                  level="body-sm"
                  className="truncate text-muted-foreground/80"
                >
                  {isShared
                    ? `May ${describePermissions(grant.rolePermissions).toLowerCase()} - their people never more.`
                    : grant.granteeName
                      ? `Asked of ${grant.granteeName}: its owners take the books up.`
                      : `Asked of ${grant.invitedEmailAddress}: they take the books up for their firm.`}
                </Typography>
              </div>
              <SimpleEnum
                label={isShared ? "Shared" : "Invited"}
                enumColor={isShared ? EnumColors.GREEN : EnumColors.ORANGE}
              />
              <RolesSelect
                roles={roles}
                value={grant.roleIdentifier}
                onChange={(role) =>
                  void changeRole(grant.identifier, role.identifier)
                }
                data-testid={`settings-accountant-grant-${grant.identifier}-role`}
              />
              <Button
                variant="secondary"
                size="sm"
                data-testid={`settings-accountant-grant-${grant.identifier}-revoke`}
                onClick={() => void revoke(grant.identifier)}
                disabled={isRevoking}
                className="text-destructive hover:text-destructive"
              >
                {isRevoking && <Spinner />}
                {isShared ? "Stop sharing" : "Cancel invitation"}
              </Button>
            </div>
            {grant.delegations.map((delegation) => (
              <div
                key={delegation.identifier}
                className="flex items-center gap-3 pl-11 pr-3 min-h-9"
                data-testid={`settings-accountant-delegation-${delegation.identifier}`}
              >
                <div className="flex text-muted-foreground/80">
                  <Icon ReactIcon={UserIcon} size="sm" />
                </div>
                <Typography level="body-sm" className="truncate flex-1 min-w-0">
                  {delegation.memberName ?? delegation.memberEmailAddress}
                </Typography>
                {/* What the server says they may do there: none where the
                    grant holds them no account in the business. */}
                <Typography
                  level="body-sm"
                  className="truncate text-muted-foreground"
                >
                  {delegation.roleName} ·{" "}
                  {delegation.memberPermissions
                    ? describePermissions(delegation.memberPermissions)
                    : "not working in these books"}
                </Typography>
              </div>
            ))}
          </div>
        );
      })}
      <form
        className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border pt-2 pb-2 flex-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          if (!pickedRole) return;
          void invite(
            entity.identifier,
            firm
              ? { firmIdentifier: firm.identifier }
              : { emailAddress: emailAddress.trim() },
            pickedRole,
            () => {
              setEmailAddress("");
              setFirm(null);
            },
          );
        }}
      >
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={UserPlusIcon} size="md" />
          </div>
        </Skeleton>
        <Input
          type="email"
          placeholder="accountant@firm.com"
          value={emailAddress}
          disabled={isLoading}
          onChange={(event) => {
            setEmailAddress(event.target.value);
            if (event.target.value) setFirm(null);
          }}
          data-testid={`settings-accountant-${entity.identifier}-email`}
          className="h-8 w-auto"
        />
        {(isLoadingFirms || firms.length > 0) && (
          <>
            <Typography level="body-sm" className="text-muted-foreground/80">
              or
            </Typography>
            <Skeleton
              loading={isLoadingFirms}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              <div
                className="w-55 shrink-0"
                data-testid={`settings-accountant-${entity.identifier}-firm`}
              >
                <Select<VerifiedFirm>
                  value={firm}
                  onChange={(picked) => {
                    setFirm(picked);
                    setEmailAddress("");
                  }}
                  data={firms}
                  getOptionKey={(option) => option.identifier}
                  size="sm"
                >
                  <Select.Trigger placeholder="A verified firm">
                    {firm ? firm.name : null}
                  </Select.Trigger>
                  <Select.Options<VerifiedFirm>
                    filterOptions={(options, filter) =>
                      options.filter((option) =>
                        (option.name ?? "")
                          .toLowerCase()
                          .includes(filter.toLowerCase()),
                      )
                    }
                    searchPlaceholder="Find a firm"
                  >
                    {(option) => (
                      <Select.Option value={option}>
                        {option.name}
                      </Select.Option>
                    )}
                  </Select.Options>
                </Select>
              </div>
            </Skeleton>
          </>
        )}
        <Skeleton
          loading={rolesLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          <RolesSelect
            roles={roles}
            value={pickedRole}
            onChange={(role) => setPickedRole(role.identifier)}
            data-testid={`settings-accountant-${entity.identifier}-invite-role`}
          />
        </Skeleton>
        <Button
          variant="ghost"
          size="sm"
          type="submit"
          disabled={
            isInviting ||
            isLoading ||
            (!emailAddress.trim() && !firm) ||
            !pickedRole
          }
          data-testid={`settings-accountant-${entity.identifier}-invite`}
        >
          {isInviting && <Spinner />}
          {grants.length === 0 ? "Share the books" : "Share with another firm"}
        </Button>
      </form>
    </SettingsViewCard>
  );
}
